import { ApolloClient } from '@apollo/client';
import { jwtDecode } from 'jwt-decode';
import { setSelectedFacility } from 'src/apollo/local-state';
import { GET_ORGANISATION_DETAILS } from 'src/components/organisations/organisations-queries';
import { getClientConfig, getEnvConfig, getRootProviderId } from 'src/config/config';
import { HEADER_ROOT_PROVIDER_ID } from 'src/constants/auth-headers';
import { CLUBSPARK_ORG_ID } from 'src/constants/orgId';
import * as classic from 'src/utils/classic-api';
import {
  clearAuthStorage,
  retrieveSelectedFacilityId,
  retrieveToken,
  storeSelectedFacilityId,
  storeToken,
} from 'src/utils/storage/local-storage';

import { StaffScope, SystemRole, VenueSystemRole } from './enums';
import { makeCsTokenHeader } from './headers';
import {
  AuthEvent,
  AuthState,
  CurrentUserVenue,
  isBrowser,
  LoginError,
  User,
  UserVenue,
  VenueWithDetails,
} from './types/auth.types';
import { transformUserVenue } from './utils';

class AuthService {
  private _token: string | null = null;
  private _user: User | null = null;
  private _userVenues: UserVenue[] | null = null;
  private _client: ApolloClient<object> | null = null;
  private _authState: AuthState | null = null;

  set client(client: ApolloClient<object> | null) {
    this._client = client;
  }

  get client() {
    return this._client;
  }

  set token(token: string | null) {
    this._token = token;
  }

  get token() {
    return this._token;
  }

  set user(user: User | null) {
    this._user = user;
  }

  get user() {
    return this._user;
  }

  set userVenues(userVenues: UserVenue[] | null) {
    this._userVenues = userVenues;
  }

  get userVenues() {
    return this._userVenues;
  }

  set authState(authState: AuthState | null) {
    this._authState = authState;
  }

  get authState() {
    return this._authState;
  }

  private emit(type: AuthEvent, payload?: any) {
    isBrowser && window.dispatchEvent(new CustomEvent(type, { detail: payload }));
  }

  private changeAuthState(authState: AuthState, error: LoginError = LoginError.NONE) {
    this.authState = authState;
    this.emit(AuthEvent.STATE_CHANGE, {
      authState,
      authError: error,
      token: this.token,
      user: this.user,
      userVenues: this.userVenues,
    });
  }

  private clearStorage() {
    clearAuthStorage();
  }

  private async clearAuthState() {
    this.token = null;
    this.user = null;
    this.userVenues = null;
    if (this.client) await this.client.clearStore();
  }

  async resetAuth(error: LoginError = LoginError.NONE) {
    this.clearStorage();
    await this.clearAuthState();
    this.changeAuthState(AuthState.UNAUTHENTICATED, error);
  }

  subscribe(eventType: AuthEvent, callback: EventListenerOrEventListenerObject) {
    isBrowser && window.addEventListener(eventType, callback);

    return {
      unsubscribe: () => {
        isBrowser && window.removeEventListener(eventType, callback);
      },
    };
  }

  private async authenticate() {
    const authSteps = [this.loadToken, this.loadUser];

    if (!getClientConfig().isSaaS) {
      authSteps.push(this.loadUserVenues);
    }

    let hasAuthError = false;
    let authError = LoginError.NONE;

    for (const step of authSteps) {
      const errorCode = await step.call(this);

      if (errorCode !== LoginError.NONE) {
        hasAuthError = true;
        authError = errorCode;
        break;
      }
    }

    if (hasAuthError) {
      await this.resetAuth(authError);
    } else {
      this.changeAuthState(AuthState.AUTHENTICATED);
    }
  }

  async start(client: ApolloClient<object>) {
    this.client = client;
    await this.authenticate();
  }

  private async isTokenValid(token: string): Promise<boolean> {
    if (this.isTokenExpired(token)) return Promise.resolve(false);
    return !(await this.isTokenIlligitimate(token));
  }

  private isTokenExpired(token: string): boolean {
    try {
      const decoded: { exp: number } = jwtDecode(token);
      if (!decoded.exp) return true;
      return decoded.exp * 1000 < Date.now();
    } catch (e) {
      return true; // If we can't decode the token, assume it's expired
    }
  }

  /**
   * Leverages the token based auth automatic fallback in the saas core authorizer
   * to check if the token is still valid whilst omitting the cookie credentials.
   */
  private async isTokenIlligitimate(token: string): Promise<boolean> {
    const res = await classic.fetchWithAuth(
      `${getEnvConfig().KUBE_BASE_URL}/saas-core-authorizer-api/v2/User/GetCurrentUser`,
      {
        credentials: 'omit',
        headers: {
          Authorization: makeCsTokenHeader(token),
        },
      },
    );
    return !res?.ok;
  }

  async authPing() {
    if (this.token && !(await this.isTokenValid(this.token))) {
      await this.resetAuth();
    }
  }

  private async loadToken() {
    try {
      let token = retrieveToken();

      if (!token || !(await this.isTokenValid(token))) {
        token = await this.fetchAndStoreToken();
      }

      this.token = token;

      return LoginError.NONE;
    } catch (err) {
      return ((err as Error).cause as LoginError) ?? LoginError.GENERIC;
    }
  }

  private async fetchAndStoreToken() {
    const tokenRes = await fetch(`${getEnvConfig().CLUBSPARK_CLASSIC_URL}/Account/Tokens?clientId=clubspark-app`, {
      credentials: 'include',
      headers: getClientConfig().useTenantSso
        ? {
            [HEADER_ROOT_PROVIDER_ID]: getRootProviderId(),
          }
        : undefined,
    });

    if (!tokenRes.ok) {
      throw new Error('Failed to fetch token', {
        cause: tokenRes.status === 401 ? LoginError.UNAUTHENTICATED : LoginError.GENERIC,
      });
    }

    const token = tokenRes.headers.get('x-api-token');

    if (!token) throw Error('No token', { cause: LoginError.GENERIC });

    storeToken(token);

    return token;
  }

  private async loadUser() {
    try {
      this.user = await this.fetchCurrentUser();
      return LoginError.NONE;
    } catch (err) {
      return ((err as Error).cause as LoginError) ?? LoginError.GENERIC;
    }
  }

  private async fetchCurrentUser() {
    const res = await classic.fetchCurrentUser();

    if (!res.ok) {
      throw new Error('Failed to fetch user', {
        cause: res.status === 401 ? LoginError.UNAUTHENTICATED : LoginError.GENERIC,
      });
    }

    const user = await res.json();

    const userSystemRoles = user?.SystemRoles ?? SystemRole.NONE;

    return {
      id: user?.ID,
      externalId: user?.ExternalId,
      firstName: user?.FirstName,
      lastName: user?.LastName,
      emailAddress: user?.EmailAddress,
      phoneNumber: user?.PhoneNumber,
      systemRoles: userSystemRoles,
      status: user?.Status,
      isFemale: user?.IsFemale,
      birthDate: user?.BirthDate,
      venueContacts: user?.VenueContacts,
      isGlobalAdmin: !!(userSystemRoles & SystemRole.SUPER_ADMINISTRATOR || userSystemRoles & SystemRole.ADMINISTRATOR),
    };
  }

  private async loadUserVenues() {
    try {
      this.userVenues = await this.fetchUserVenues();
      return LoginError.NONE;
    } catch (err) {
      return ((err as Error).cause as LoginError) ?? LoginError.GENERIC;
    }
  }

  private async fetchUserVenues() {
    const res = await classic.fetchUserVenues();

    if (!res.ok) {
      throw new Error('Failed to fetch user venues', {
        cause: res.status === 401 ? LoginError.UNAUTHENTICATED : LoginError.GENERIC,
      });
    }

    const currentUserVenues: CurrentUserVenue[] = (await res.json())?.Venues ?? [];
    const lastSelectedFacilityId = retrieveSelectedFacilityId(this.user);
    const venueIds = currentUserVenues.map((venue) => venue.VenueID).filter((id) => id !== CLUBSPARK_ORG_ID);

    // If user is global admin, we need to fetch the details of the root provider and the last selected facility
    // as it might be a facility they don't have an admin role at
    const venueIdsForDetails = Array.from(
      new Set(
        [...venueIds, ...(this.user?.isGlobalAdmin ? [getRootProviderId(), lastSelectedFacilityId] : [])].filter(
          Boolean,
        ),
      ),
    );

    // We leverage the Org service to fetch the details of only the venues within the same tenant based on the rootProviderId
    const venuesDetails = await this.client?.query({
      query: GET_ORGANISATION_DETAILS,
      variables: {
        organisationIds: venueIdsForDetails,
      },
      fetchPolicy: 'network-only',
    });

    const venuesWithDetails: VenueWithDetails[] = venuesDetails?.data?.getOrganisationsByIds?.filter(Boolean) ?? [];

    const userVenuesFromThisTenant = venuesWithDetails.map((venue) => {
      return transformUserVenue(
        venue,
        currentUserVenues.find((cuv) => cuv.VenueID === venue.organisationId),
      );
    });

    const { validVenues: adminVenues, hasValidVenueWithNoScopes } = this.validateUserVenues(userVenuesFromThisTenant, {
      ignoreScopes: !!getClientConfig().ignoreScopes,
      isGlobalAdmin: this.user?.isGlobalAdmin ?? false,
    });

    if (!adminVenues.length) {
      throw new Error('No valid venues', {
        cause: hasValidVenueWithNoScopes ? LoginError.INVALID_SCOPE : LoginError.NON_ADMIN,
      });
    }

    const setSelectedFacilityData = (userVenues: UserVenue[]) => {
      // We try and find if the last selected facility is in the userVenues, otherwise we default
      // to the first one in the list
      const selectedFacility =
        userVenues.find((v) => lastSelectedFacilityId && v.id === lastSelectedFacilityId) ?? userVenues[0];

      // If we still haven't found a selected facility, we default to the first one in the userVenues

      if (selectedFacility && this.client) {
        setSelectedFacility(selectedFacility, this.client);
        storeSelectedFacilityId(selectedFacility.id, this.user);
      }
    };

    setSelectedFacilityData(adminVenues);

    return adminVenues;
  }

  private validateUserVenues(venues: UserVenue[], options: { ignoreScopes: boolean; isGlobalAdmin: boolean }) {
    // In order to log in a user must meet the following criteria:
    //  - Have a valid module scope (e.g. tournaments/staff/ranking) for a venue AND either
    //     - have an admin or superadmin scope at the venue OR
    //     - have an admin or superadmin venue system role at the venue (for backwards compat)
    const hasAdminRole = (role: VenueSystemRole) =>
      [VenueSystemRole.ADMINISTRATOR, VenueSystemRole.SUPER_ADMINISTRATOR].some((ar) => role & ar);

    const hasAdminScope = (scope: StaffScope) => [StaffScope.ADMIN, StaffScope.SUPERADMIN].some((as) => scope & as);

    const hasModuleScope = (scope: StaffScope) =>
      [
        StaffScope.TOURNAMENTS,
        StaffScope.RANKINGS,
        StaffScope.REPORTS,
        StaffScope.STAFF,
        StaffScope.PLAYERS,
        StaffScope.PLAYTRACKER,
        StaffScope.SUSPENSIONS,
        StaffScope.MEMBERSHIPS,
        StaffScope.ORGANISATIONS,
        StaffScope.PROGRAMMES,
        StaffScope.EVENTS,
        StaffScope.COACHES,
        StaffScope.DISCOUNTS,
        StaffScope.LEAGUES,
        StaffScope.AREAS,
      ].some((ms) => scope & ms);

    const { ignoreScopes, isGlobalAdmin } = options;

    let hasValidVenueWithNoScopes = false;

    if (isGlobalAdmin) return { validVenues: venues, hasValidVenueWithNoScopes };

    const validVenues = venues?.filter?.((v) => {
      if (ignoreScopes) {
        const role: VenueSystemRole = v.venueSystemRoles ?? VenueSystemRole.NONE;
        return hasAdminRole(role);
      }

      const scope: StaffScope = v.scope ?? StaffScope.NONE;

      // The new way, all in scopes
      if (hasAdminScope(scope) && hasModuleScope(scope)) {
        // you need admin/superadmin AND a module scope
        return true;
      } else if (hasAdminScope(scope)) {
        hasValidVenueWithNoScopes = true;
      }

      return false;
    });

    return { validVenues, hasValidVenueWithNoScopes };
  }
}

const authService = new AuthService();
export default authService;
